import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Phone, Mail, Heart, FileText } from 'lucide-react';

function Home() {
  useEffect(() => {

    const ChartComponent = () => {
      const data = [
        {
          name: 'Enrollment Rates',
          'Q3 2024': 45,
          'Q4 2024': 62,
        },
        {
          name: 'Demo Requests',
          'Q3 2024': 28,
          'Q4 2024': 40,
        },
        {
          name: 'Conversion Rate',
          'Q3 2024': 18,
          'Q4 2024': 30,
        },
      ];

      const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
          return (
            <div className="bg-gray-800 text-white p-4 rounded-lg shadow-xl border-2 border-orange-500">
              <p className="font-bold mb-2">{`Metrics: ${label}`}</p>
              {payload.map((entry, index) => (
                <p key={index} style={{ color: entry.color }}>
                  {`${entry.name}: ${entry.value}`}
                </p>
              ))}
            </div>
          );
        }
        return null;
      };

      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#8B4513" opacity={0.3} />
            <XAxis dataKey="name" stroke="#000" style={{ fontSize: '14px', fontWeight: 'bold' }} />
            <YAxis stroke="#000" domain={[0, 75]} ticks={[0, 25, 50, 75]} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.1)' }} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="square"
            />
            <Bar dataKey="Q3 2024" fill="#5D4037" radius={[8, 8, 0, 0]} />
            <Bar dataKey="Q4 2024" fill="#6D4C41" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    };
    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable certain keyboard shortcuts
    const handleKeyDown = (e) => {
      // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection and copy
    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('dragstart', handleDragStart);

    // Disable text selection via CSS
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
    };
  }, []);

  return (
    <div className="bg-black text-white overflow-x-hidden w-full">
      {/* Navbar */}
      <Navbar />

      {/* Protection styles for images */}
      <style jsx>{`
        img {
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
        html, body {
          overflow-x: hidden;
          max-width: 100%;
        }
      `}</style>

      {/* Background stars effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full opacity-50"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

    {/* Hero Section */}
    <section id="home" className="flex items-center px-4 sm:px-8 lg:px-24 py-12 sm:py-20 pt-24 sm:pt-32">
    <div className="max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        
        {/* Left Content */}
        <div className="space-y-8 z-10">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            Marketing EdTech Products<br />
            </h1>
            
            <p className="text-gray-400 text-base lg:text-lg">
            Comprehensive strategies for EasyLearn LMS, EasyTutor, Skill Master & ISF Athena across multiple marketing channels
            </p>

            <div className="flex flex-wrap gap-3 sm:gap-4">
            <button className="px-8 py-3 bg-orange-500 text-white rounded-md font-medium hover:bg-orange-600 transition">
                Get Started
            </button>
            <button className="px-8 py-3 border border-white text-white rounded-md font-medium hover:bg-white hover:text-black transition">
                Learn More
            </button>
            </div>
        </div>

        {/* Right Image - Computer Lab Scene */}
        <div className="relative z-10 overflow-hidden rounded-3xl">
            <div className="relative rounded-3xl overflow-hidden border-1 border-cyan-400 shadow-2xl shadow-cyan-500/50 h-64 sm:h-96">
            <img 
                src="/Assests/home_page.png" 
                alt="EdTech Computer Lab" 
                className="w-full h-full object-cover"
            />
            </div>
            
            {/* Floating glow effects */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/30 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-cyan-500/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
        </div>
        
        </div>
    </div>
    </section>

      {/* Chapter 1 Section with Stats */}
      <section id="about" className="relative px-4 sm:px-8 lg:px-24 py-20">
        <div className="w-full">
          
         <h3 className="text-4xl lg:text-5xl font-bold px-0 sm:px-4 lg:px-32 mb-8 text-white">
            The EdTech Market Landscape 
         </h3>

          <p className="text-gray-400 text-base lg:text-xl leading-relaxed sm:px-0 lg:px-32 mb-28">
            The educational technology sector stands at an unprecedented inflection point. Digital transformation has fundamentally reshaped how we learn, teach, and deliver knowledge. Understanding this evolving landscape is crucial for positioning our innovative EdTech solutions effectively in today's competitive marketplace.
          </p>

          {/* Stats Section */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 lg:px-32 mb-12">
            
            {/* Market Projection */}
            <div className="text-center">
              <h4 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
                15%
              </h4>
              <h5 className="text-xl font-bold text-white mb-3">
                Market Growth
              </h5>
              <p className="text-gray-400">
                Global EdTech market grew by nearly 15% last year
              </p>
            </div>

            {/* Annual Growth */}
            <div className="text-center">
              <h4 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
                13.3%
              </h4>
              <h5 className="text-xl font-bold text-white mb-3">
                Annual Expansion
              </h5>
              <p className="text-gray-400">
                Average yearly growth rate over the past few years
              </p>
            </div>

            {/* New Players */}
            <div className="text-center">
              <h4 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
                10K+
              </h4>
              <h5 className="text-xl font-bold text-white mb-3">
                New Startups
              </h5>
              <p className="text-gray-400">
                EdTech ventures launched worldwide in 2024
              </p>
            </div>

          </div>

          {/* Bottom Description */}
          <p className="text-white text-base lg:text-lg leading-relaxed sm:px-4 lg:px-32">
            The global EdTech market's explosive growth is driven by hybrid learning adoption and AI-powered personalization. Competition intensifies as innovative players enter the digital education space, creating both opportunities and challenges for established solutions seeking market dominance.
          </p>

        </div>
      </section>

      {/* Understanding Today's Education Audience Section */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Orange Background Container */}
          <div className="bg-orange-500 rounded-3xl p-4 sm:p-8 lg:p-16">
            {/* Dark Brown Inner Container */}
            <div className="bg-gradient-to-br from-amber-950 to-stone-900 rounded-2xl p-5 sm:p-8 lg:p-12">
              
              {/* Main Heading */}
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-12">
                Understanding Today's Education Audience
              </h2>

              {/* Content Grid */}
              <div className="grid lg:grid-cols-2 gap-6 sm:gap-12">
                
                {/* Left Content - Features */}
                <div className="space-y-10">
                  
                  {/* Gen Z & Alpha Preferences */}
                  <div className="space-y-4">
                    <div className="w-12 h-12 text-orange-500">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      Gen Z & Alpha Preferences
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      Bite-sized, interactive, authentic content delivered through mobile-first platforms
                    </p>
                  </div>

                  {/* Parent & Institution Needs */}
                  <div className="space-y-4">
                    <div className="w-12 h-12 text-orange-500">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      Parent & Institution Needs
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      Transparency, measurable outcomes, and established trust in educational providers
                    </p>
                  </div>

                  {/* AI-Enabled Targeting */}
                  <div className="space-y-4">
                    <div className="w-12 h-12 text-orange-500">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      AI-Enabled Targeting
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      Hyper-personalized marketing and precise audience segmentation capabilities
                    </p>
                  </div>

                </div>

                {/* Right Image */}
                <div className="flex items-center justify-center">
                  <div className="rounded-2xl overflow-hidden shadow-2xl">
                    <img 
                      src="/Assests/home_page_1.png" 
                      alt="Education Audience" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Spotlight Section */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20">
        <div className="max-w-7xl mx-auto">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-orange-500 mb-6">
            Product Spotlight
          </h2>

          {/* Subheading */}
          <h3 className="text-xl text-white-400 mb-8">
            What Sets Our EdTech Solutions Apart
          </h3>

          {/* Description */}
          <p className="text-gray-400 text-lg leading-relaxed mb-20 max-w-5xl">
            Our comprehensive suite of educational technology products represents the pinnacle of innovation in digital learning. Each solution addresses specific market needs while maintaining seamless integration capabilities and user-centric design principles that drive measurable educational outcomes.
          </p>

          {/* EasyLearn LMS Section */}
          <div className="mb-0">
            <h3 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              EasyLearn LMS
            </h3>
            <h4 className="text-2xl lg:text-3xl font-bold text-white mb-12">
              Scalable Learning Management
            </h4>

            {/* Feature Cards Grid */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
              
              {/* Customizable Platform */}
              <div className="bg-black border-2 border-orange-500 rounded-2xl p-8 hover:bg-gray-900 transition">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-white mb-4">
                  Customizable Platform
                </h4>
                <p className="text-gray-400 leading-relaxed">
                  Tailored for schools and enterprises with flexible configuration options
                </p>
              </div>

              {/* AI-Powered Analytics */}
              <div className="bg-black border-2 border-orange-500 rounded-2xl p-8 hover:bg-gray-900 transition">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-white mb-4">
                  AI-Powered Analytics
                </h4>
                <p className="text-gray-400 leading-relaxed">
                  Advanced tracking and insights for learner progress and engagement metrics
                </p>
              </div>

              {/* Seamless Integration */}
              <div className="bg-black border-2 border-orange-500 rounded-2xl p-8 hover:bg-gray-900 transition">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-white mb-4">
                  Seamless Integration
                </h4>
                <p className="text-gray-400 leading-relaxed">
                  Compatible with existing systems and third-party educational tools
                </p>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* EasyTutor Section */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-5">
        <div className="max-w-7xl mx-auto">
            <div className="mb-16">
            <div className="bg-orange-500 rounded-3xl p-4 sm:p-8 lg:p-16">
                <div className="bg-gradient-to-br from-amber-950 to-stone-900 rounded-2xl p-5 sm:p-8 lg:p-12">
                
                <h3 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                    EasyTutor
                </h3>
                <h4 className="text-2xl lg:text-3xl font-bold text-white mb-12">
                    Personalized Tutoring Software
                </h4>

                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
                    
                    <div className="space-y-4">
                    <div className="text-orange-500 font-bold text-lg border-b-2 border-orange-500 pb-2 mb-4">
                        01
                    </div>
                    <h5 className="text-xl font-bold text-white">
                        Real-Time Virtual Classrooms
                    </h5>
                    <p className="text-gray-300 leading-relaxed">
                        Interactive tools and multimedia support for engaging learning experiences
                    </p>
                    </div>

                    <div className="space-y-4">
                    <div className="text-orange-500 font-bold text-lg border-b-2 border-orange-500 pb-2 mb-4">
                        02
                    </div>
                    <h5 className="text-xl font-bold text-white">
                        Adaptive Learning Paths
                    </h5>
                    <p className="text-gray-300 leading-relaxed">
                        AI algorithms customize content delivery based on individual student needs
                    </p>
                    </div>

                    <div className="space-y-4">
                    <div className="text-orange-500 font-bold text-lg border-b-2 border-orange-500 pb-2 mb-4">
                        03
                    </div>
                    <h5 className="text-xl font-bold text-white">
                        Global Trust Network
                    </h5>
                    <p className="text-gray-300 leading-relaxed">
                        Proven success with 500+ tutoring centers worldwide and growing
                    </p>
                    </div>

                </div>
                </div>
            </div>
            </div>
        </div>
        </section>

        {/* Skill Master Section */}
        <section className="relative px-4 sm:px-8 lg:px-24 py-5">
        <div className="max-w-7xl mx-auto">
            <div className="mb-16">
            <div className="bg-orange-500 rounded-3xl p-4 sm:p-8 lg:p-16">
                <div className="bg-gradient-to-br from-amber-950 to-stone-900 rounded-2xl p-5 sm:p-8 lg:p-12">
                
                <h3 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{whiteSpace: 'inherit'}}>
                    Skill Master
                </h3>
                <h4 className="text-2xl lg:text-3xl font-bold text-white mb-12" style={{whiteSpace: 'inherit'}}>
                    Upskilling for the Future Workforce
                </h4>

                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    
                    <div className="space-y-8">
                    
                    <div className="flex flex-wrap gap-3 sm:gap-4">
                        <div className="flex-shrink-0 mt-1">
                        <div className="w-8 h-8 border-2 border-orange-500 rounded-lg"></div>
                        </div>
                        <div>
                        <h5 className="text-xl font-bold text-white mb-2" style={{whiteSpace: 'inherit'}}>
                            Micro-Credentialing System
                        </h5>
                        <p className="text-gray-300 leading-relaxed" style={{whiteSpace: 'inherit'}}>
                            Gamified learning modules with industry-recognized certifications
                        </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3 sm:gap-4">
                        <div className="flex-shrink-0 mt-1">
                        <div className="w-8 h-8 border-2 border-orange-500 rounded-lg"></div>
                        </div>
                        <div>
                        <h5 className="text-xl font-bold text-white mb-2" style={{whiteSpace: 'inherit'}}>
                            Corporate Training Focus
                        </h5>
                        <p className="text-gray-300 leading-relaxed" style={{whiteSpace: 'inherit'}}>
                            Measurable ROI through strategically targeted skill development programs
                        </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3 sm:gap-4">
                        <div className="flex-shrink-0 mt-1">
                        <div className="w-8 h-8 border-2 border-orange-500 rounded-lg"></div>
                        </div>
                        <div>
                        <h5 className="text-xl font-bold text-white mb-2" style={{whiteSpace: 'inherit'}}>
                            Industry Partnerships
                        </h5>
                        <p className="text-gray-300 leading-relaxed" style={{whiteSpace: 'inherit'}}>
                            Collaboration with leading companies for authentic certification pathways
                        </p>
                        </div>
                    </div>

                    </div>

                    <div className="flex items-center justify-center">
                    <div className="rounded-2xl overflow-hidden shadow-2xl">
                        <img 
                        src="/Assests/home_page_2.png" 
                        alt="Skill Master Workforce" 
                        className="w-full h-full object-cover"
                        />
                    </div>
                    </div>

                </div>
                </div>
            </div>
            </div>
        </div>
        </section>

        {/* ISF Athena Section */}
        <section className="relative px-4 sm:px-8 lg:px-24 py-5">
        <div className="max-w-7xl mx-auto">
            <div className="mb-16">
            <div className="bg-orange-500 rounded-3xl p-4 sm:p-8 lg:p-16">
                <div className="bg-gradient-to-br from-amber-950 to-stone-900 rounded-2xl p-5 sm:p-8 lg:p-12">
                
                <h3 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{whiteSpace: 'inherit'}}>
                    ISF Athena
                </h3>
                <h4 className="text-2xl lg:text-3xl font-bold text-white mb-12" style={{whiteSpace: 'inherit'}}>
                    Comprehensive Online Education Hub
                </h4>

                <div className="grid lg:grid-cols-3 gap-6 sm:gap-12 items-center">
                    
                    {/* Left */}
                    <div className="text-right">
                    <h5 className="text-xl font-bold text-white mb-3" style={{whiteSpace: 'inherit'}}>
                        Multi-Subject Courses
                    </h5>
                    <p className="text-gray-300 leading-relaxed" style={{whiteSpace: 'inherit'}}>
                        K-12 and adult learning programs covering diverse academic disciplines
                    </p>
                    </div>

                    {/* Center - Image */}
                    <div className="flex items-center justify-center">
                    <div className="rounded-2xl">
                        <img 
                        src="/Assests/isf_diagram.png" 
                        alt="ISF Athena Diagram" 
                        className="w-full h-full object-cover"
                        />
                    </div>
                    </div>

                    {/* Right */}
                    <div className="space-y-8">
                    <div>
                        <h5 className="text-xl font-bold text-white mb-3" style={{whiteSpace: 'inherit'}}>
                        Engaging Content
                        </h5>
                        <p className="text-gray-300 leading-relaxed" style={{whiteSpace: 'inherit'}}>
                        Interactive video lessons and live workshop sessions
                        </p>
                    </div>

                    <div>
                        <h5 className="text-xl font-bold text-white mb-3" style={{whiteSpace: 'inherit'}}>
                        Strong Community
                        </h5>
                        <p className="text-gray-300 leading-relaxed" style={{whiteSpace: 'inherit'}}>
                        Mentorship programs and peer-to-peer learning networks
                        </p>
                    </div>
                    </div>

                </div>
                </div>
            </div>
            </div>
        </div>
        </section>

        {/* Chapter 3 Section */}
        <section className="relative px-4 sm:px-8 lg:px-24 py-20">
        <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            
            {/* Left Content */}
            <div className="space-y-8">
                
                <h3 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-orange-500">
                Multi-Channel Marketing Models
                </h3>

                <h4 className="text-xl font-bold text-white">
                EdTech Growth Strategies
                </h4>

                <p className="text-gray-400 text-lg leading-relaxed">
                Success in EdTech marketing requires a sophisticated multi-channel approach that reaches diverse audiences through their preferred platforms. Our comprehensive strategy leverages traditional and digital channels to maximize reach, engagement, and conversion rates across all target segments.
                </p>
            </div>

            {/* Right Image */}
            <div className="relative z-10">
            <div className="relative rounded-3xl overflow-hidden border-1 border-cyan-400 shadow-2xl shadow-cyan-500/50" style={{height: '400px'}}>
                <img 
                src="/Assests/home_page_3.png" 
                alt="Multi-Channel Marketing" 
                className="w-full h-full object-cover"
                />
            </div>
            </div>

            </div>
        </div>
        </section>

        {/* Franchise Model Section */}
        <section className="relative px-4 sm:px-8 lg:px-24 py-8">
        <div className="max-w-7xl mx-auto">
            {/* Orange Background Container */}
            <div className="bg-orange-500 rounded-3xl p-4 sm:p-8 lg:p-16">
            {/* Dark Brown Inner Container */}
            <div className="bg-gradient-to-br from-amber-950 to-stone-900 rounded-2xl p-5 sm:p-8 lg:p-12">
                
                {/* Main Heading */}
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Franchise Model
                </h2>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-12">
                Expanding Reach Locally
                </h3>

                {/* Three Columns */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                
                {/* Trusted Brand Names */}
                <div className="bg-black p-8 border-2 border-white-500 border-r-0">
                    <div className="text-center space-y-4">
                    <div className="w-12 h-12 mx-auto text-orange-500">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
                        </svg>
                    </div>
                    <h4 className="text-xl font-bold text-white">
                        Trusted Brand Names
                    </h4>
                    <p className="text-gray-400 leading-relaxed text-sm">
                        Partners operate under established, credible educational brands
                    </p>
                    </div>
                </div>

                {/* Localized Marketing */}
                <div className="bg-black p-8 border-2 border-white-500 border-r-0">
                    <div className="text-center space-y-4">
                    <div className="w-12 h-12 mx-auto text-orange-500">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                        </svg>
                    </div>
                    <h4 className="text-xl font-bold text-white">
                        Localized Marketing
                    </h4>
                    <p className="text-gray-400 leading-relaxed text-sm">
                        Tailored support and campaigns for specific regional needs
                    </p>
                    </div>
                </div>

                {/* Proven Success */}
                <div className="bg-black p-8 border-2 border-white-500">
                    <div className="text-center space-y-4">
                    <div className="w-12 h-12 mx-auto text-orange-500">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h4 className="text-xl font-bold text-white">
                        Proven Success
                    </h4>
                    <p className="text-gray-400 leading-relaxed text-sm">
                        EasyTutor's franchise network achieved 40% growth in 2024
                    </p>
                    </div>
                </div>

                </div>
            </div>
            </div>
        </div>
        </section>

        {/* Affiliate Marketing Section */}
        <section className="relative px-4 sm:px-8 lg:px-24 py-8">
        <div className="max-w-7xl mx-auto">
            {/* Orange Background Container */}
            <div className="bg-orange-500 rounded-3xl p-4 sm:p-8 lg:p-16">
            {/* Dark Brown Inner Container */}
            <div className="bg-gradient-to-br from-amber-950 to-stone-900 rounded-2xl p-5 sm:p-8 lg:p-12">
                
                {/* Main Heading */}
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Affiliate Marketing
                </h2>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-12">
                Leveraging Influencers & Educators
                </h3>

                {/* Grid Layout */}
                <div className="grid lg:grid-cols-2 gap-6 sm:gap-12">
                
                {/* Left Content */}
                <div className="space-y-8">
                    
                    {/* Strategic Collaborations */}
                    <div className="space-y-4">
                    <h4 className="text-2xl font-bold text-white">
                        Strategic Collaborations
                    </h4>
                    <p className="text-gray-300 leading-relaxed">
                        Partnerships with education bloggers, tech influencers, and thought leaders in the learning space drive authentic engagement and credible endorsements.
                    </p>
                    </div>

                    {/* Commission-Based Growth */}
                    <div className="space-y-4">
                    <h4 className="text-2xl font-bold text-white">
                        Commission-Based Growth
                    </h4>
                    <p className="text-gray-300 leading-relaxed">
                        Performance-driven referral programs generate qualified leads while maintaining cost-effective acquisition strategies.
                    </p>
                    </div>

                </div>

                {/* Right Content - Success Story */}
                <div className="bg-amber-900/50 rounded-xl p-8 border-2 border-orange-500/30">
                    <div className="flex items-start gap-4">
                    <div className="w-8 h-8 text-orange-500 flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                        </svg>
                    </div>
                    <div className="space-y-3">
                        <h5 className="text-xl font-bold text-white">
                        Success Story
                        </h5>
                        <p className="text-white-300 leading-relaxed">
                        Skill Master's affiliate program increased signups by 25% in just 6 months through targeted educator partnerships.
                        </p>
                    </div>
                    </div>
                </div>

                </div>
            </div>
            </div>
        </div>
        </section>

        {/* Social Media Marketing Section */}
        <section className="relative px-4 sm:px-8 lg:px-24 py-20">
          <div className="max-w-7xl mx-auto">
            
            {/* Heading */}
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Social Media Marketing
              </h2>
              <h3 className="text-2xl lg:text-3xl font-bold text-white">
                Engaging Gen-Z & Parents
              </h3>
            </div>

            {/* Three Circular Images with Content */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-12">
              
              {/* Short-Form Video Content */}
              <div className="text-center space-y-6">
                <div className="w-64 h-64 mx-auto rounded-full overflow-hidden border-4 border-white shadow-2xl">
                  <img 
                    src="/Assests/home_page_4.png" 
                    alt="Short-Form Video Content" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-2xl font-bold text-white">
                  Short-Form Video Content
                </h4>
                <p className="text-white leading-relaxed">
                  TikTok, Instagram Reels, and YouTube Shorts showcase product features through engaging, snackable content
                </p>
              </div>

              {/* User-Generated Content */}
              <div className="text-center space-y-6">
                <div className="w-64 h-64 mx-auto rounded-full overflow-hidden border-4 border-white shadow-2xl">
                  <img 
                    src="/Assests/home_page_5.png" 
                    alt="User-Generated Content" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-2xl font-bold text-white">
                  User-Generated Content
                </h4>
                <p className="text-white leading-relaxed">
                  Testimonials and success stories from real users build trust and authenticity
                </p>
              </div>

              {/* AI-Optimized Campaigns */}
              <div className="text-center space-y-6">
                <div className="w-64 h-64 mx-auto rounded-full overflow-hidden border-4 border-white shadow-2xl">
                  <img 
                    src="/Assests/home_page_6.png" 
                    alt="AI-Optimized Campaigns" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-2xl font-bold text-white">
                  AI-Optimized Campaigns
                </h4>
                <p className="text-white leading-relaxed">
                  Paid advertising with precise audience targeting and automated optimization algorithms
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* Email Marketing Section */}
        <section
          className="relative px-4 sm:px-8 py-16 sm:py-24 bg-cover bg-center bg-no-repeat overflow-x-hidden"
          style={{
            backgroundImage: "url('/Assests/home_page.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-60"></div>

          {/* Main container */}
          <div className="relative z-10 max-w-4xl mx-auto text-white">

            {/* Headings */}
            <div className="text-center mb-10 sm:mb-14">
              <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
                Email Marketing
              </h2>
              <h3 className="text-lg sm:text-2xl lg:text-3xl font-semibold text-gray-200">
                Personalized Automation & Dynamic Segmentation
              </h3>
            </div>

            {/* 3 Cards stacked vertically with connecting line */}
            <div className="relative flex flex-col items-center gap-0">

              {/* Card 1 — AI-Driven Campaigns */}
              <div className="w-full max-w-lg bg-black/50 border border-orange-500/40 backdrop-blur-md rounded-2xl p-6 text-center">
                <div className="w-10 h-10 rounded-full border-4 border-orange-500 bg-black flex items-center justify-center mx-auto mb-4">
                  <span className="text-lg font-bold text-orange-500">1</span>
                </div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-2">
                  AI-Driven Campaigns
                </h4>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  Personalized content based on user behavior, engagement patterns, and learning preferences
                </p>
              </div>

              {/* Connector line */}
              <div className="w-0.5 h-8 bg-orange-500"></div>

              {/* Card 2 — Performance Boost */}
              <div className="w-full max-w-lg bg-black/50 border border-orange-500/40 backdrop-blur-md rounded-2xl p-6 text-center">
                <div className="w-10 h-10 rounded-full border-4 border-orange-500 bg-black flex items-center justify-center mx-auto mb-4">
                  <span className="text-lg font-bold text-orange-500">2</span>
                </div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-2">
                  Performance Boost
                </h4>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  20% increase in open rates through strategic personalization and timing optimization
                </p>
              </div>

              {/* Connector line */}
              <div className="w-0.5 h-8 bg-orange-500"></div>

              {/* Card 3 — Automated Follow-Ups */}
              <div className="w-full max-w-lg bg-black/50 border border-orange-500/40 backdrop-blur-md rounded-2xl p-6 text-center">
                <div className="w-10 h-10 rounded-full border-4 border-orange-500 bg-black flex items-center justify-center mx-auto mb-4">
                  <span className="text-lg font-bold text-orange-500">3</span>
                </div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-2">
                  Automated Follow-Ups
                </h4>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  Seamless sequences after webinars, product demos, and website interactions
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* Contact Marketing & Cold Calls Section */}
        <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-black">
          <div className="max-w-7xl mx-auto">
            
            {/* Heading */}
            <div className="mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-orange-500 mb-4">
                Contact Marketing & Cold Calls
              </h2>
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-200">
                Human Touch in a Digital World
              </h3>
            </div>

            {/* Grid Layout */}
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              
              {/* Left - Image */}
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="/Assests/home_page_8.png" 
                    alt="Contact Marketing" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Right - Content */}
              <div className="space-y-10">
                
                {/* Targeted Outreach Strategy */}
                <div className="flex flex-wrap gap-3 sm:gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-3 h-3 rounded-full bg-orange-500 mt-2"></div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-2xl font-bold text-white">
                      Targeted Outreach Strategy
                    </h4>
                    <p className="text-gray-300 leading-relaxed">
                      Direct contact with school administrators and corporate L&D managers
                    </p>
                  </div>
                </div>

                {/* Data-Driven Lead Lists */}
                <div className="flex flex-wrap gap-3 sm:gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-3 h-3 rounded-full bg-orange-500 mt-2"></div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-2xl font-bold text-white">
                      Data-Driven Lead Lists
                    </h4>
                    <p className="text-gray-300 leading-relaxed">
                      CRM integration ensures efficient follow-up and relationship management
                    </p>
                  </div>
                </div>

                {/* Outcome-Based Selling */}
                <div className="flex flex-wrap gap-3 sm:gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-3 h-3 rounded-full bg-orange-500 mt-2"></div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-2xl font-bold text-white">
                      Outcome-Based Selling
                    </h4>
                    <p className="text-gray-300 leading-relaxed">
                      Scripts focused on addressing specific pain points and demonstrating value
                    </p>
                  </div>
                </div>

              </div>

            </div>
          </div>
        </section>
        

        {/* Integrating AI & Data Section */}
        <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-black">
          <div className="max-w-7xl mx-auto">
            
            {/* Top Heading */}
            <div className="mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Integrating AI & Data
              </h2>
              <h3 className="text-2xl font-bold text-white mb-8">
                Smarter Marketing Through Technology
              </h3>
              <p className="text-gray-300 text-lg leading-relaxed max-w-5xl">
                The convergence of artificial intelligence and marketing analytics has revolutionized how EdTech companies engage prospects and customers. Our data-driven approach leverages cutting-edge AI tools to optimize every touchpoint in the customer journey, delivering unprecedented results.
              </p>
            </div>

            {/* Background Image Container */}
            <div className="relative rounded-3xl overflow-hidden mt-16">
              {/* Background Image */}
              <div className="absolute inset-0">
                <img 
                  src="/Assests/home_page_9.png" 
                  alt="AI & Data Background" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60"></div>
              </div>

              {/* Content */}
              <div className="relative z-10 p-12 lg:p-16">
                
                {/* AI-Powered Personalization Heading */}
                <div className="mb-12">
                  <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                    AI-Powered Personalization
                  </h3>
                  <h4 className="text-2xl lg:text-3xl font-bold text-white">
                    Chatbots & Dynamic Segmentation
                  </h4>
                </div>

                {/* Three Cards */}
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
                  
                  {/* Intelligent Chatbots */}
                  <div className="bg-black/70 backdrop-blur-sm border-2 border-orange-500 rounded-2xl p-8 hover:bg-black/80 transition">
                    <h5 className="text-xl font-bold text-white mb-4">
                      Intelligent Chatbots
                    </h5>
                    <p className="text-gray-300 leading-relaxed">
                      Handle 80% of routine inquiries, freeing sales teams for high-value prospect interactions and relationship building
                    </p>
                  </div>

                  {/* Dynamic Segmentation */}
                  <div className="bg-black/70 backdrop-blur-sm border-2 border-orange-500 rounded-2xl p-8 hover:bg-black/80 transition">
                    <h5 className="text-xl font-bold text-white mb-4">
                      Dynamic Segmentation
                    </h5>
                    <p className="text-gray-300 leading-relaxed">
                      Prospects grouped by engagement level, behavior patterns, and conversion probability for targeted messaging
                    </p>
                  </div>

                  {/* Real-Time Optimization */}
                  <div className="bg-black/70 backdrop-blur-sm border-2 border-orange-500 rounded-2xl p-8 hover:bg-black/80 transition">
                    <h5 className="text-xl font-bold text-white mb-4">
                      Real-Time Optimization
                    </h5>
                    <p className="text-gray-300 leading-relaxed">
                      AI algorithms optimize ad spend, content delivery timing, and channel selection automatically
                    </p>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Unlocking Growth Section */}
<section className="relative px-4 sm:px-8 lg:px-24 py-20">
          <div className="max-w-7xl mx-auto">
            
            {/* Heading */}
            <div className="mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold text-orange-500 mb-4">
                Data-Driven Insights
              </h2>
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-12">
                Campaign Success Through Analytics
              </h3>
            </div>

            {/* Custom Bar Chart */}
            <div className="backdrop-blur-sm rounded-2xl p-8 mb-12 max-w-5xl mx-auto">
              <div className="relative h-96">
                {/* Y-Axis Labels */}
                <div className="absolute left-0 top-0 bottom-12 flex flex-col justify-between text-white font-semibold">
                  <span>100</span>
                  <span>75</span>
                  <span>50</span>
                  <span>25</span>
                </div>

                {/* Chart Area */}
                <div className="ml-12 h-full flex items-end justify-around pb-12 border-b-2 border-l-2 border-white/20 relative">
                  
                  {/* Grid Lines */}
                  <div className="absolute inset-0 flex flex-col justify-between pointer-events-none mb-25">
                    <div className="border-t border-white/30"></div>
                    <div className="border-t border-white/30"></div>
                    <div className="border-t border-white/30"></div>
                  </div>

                  {/* Enrollment Rates */}
                  <div className="flex flex-col items-center gap-2 flex-1 relative group">
                    <div className="flex gap-2 items-end w-full justify-center">
                      {/* Q3 2024 Bar */}
                      <div className="relative w-16">
                        <div 
                          className="bg-amber-900 rounded-t-lg transition-all duration-300 hover:bg-amber-800 cursor-pointer"
                          style={{height: '240px'}}
                        >
                          {/* Tooltip */}
                          <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            Q3 2024: 45
                          </div>
                        </div>
                      </div>
                      {/* Q4 2024 Bar */}
                      <div className="relative w-16">
                        <div 
                          className="bg-amber-800 rounded-t-lg transition-all duration-300 hover:bg-amber-700 cursor-pointer"
                          style={{height: '330px'}}
                        >
                          {/* Tooltip */}
                          <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            Q4 2024: 62
                          </div>
                        </div>
                      </div>
                    </div>
                    <span className="text-white font-semibold text-sm mt-2">Enrollment Rates</span>
                  </div>

                  {/* Demo Requests */}
                  <div className="flex flex-col items-center gap-2 flex-1 relative group">
                    <div className="flex gap-2 items-end w-full justify-center">
                      {/* Q3 2024 Bar */}
                      <div className="relative w-16">
                        <div 
                          className="bg-amber-900 rounded-t-lg transition-all duration-300 hover:bg-amber-800 cursor-pointer"
                          style={{height: '149px'}}
                        >
                          {/* Tooltip */}
                          <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            Q3 2024: 28
                          </div>
                        </div>
                      </div>
                      {/* Q4 2024 Bar */}
                      <div className="relative w-16">
                        <div 
                          className="bg-amber-800 rounded-t-lg transition-all duration-300 hover:bg-amber-700 cursor-pointer"
                          style={{height: '213px'}}
                        >
                          {/* Tooltip */}
                          <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            Q4 2024: 40
                          </div>
                        </div>
                      </div>
                    </div>
                    <span className="text-white font-semibold text-sm mt-2">Demo Requests</span>
                  </div>

                  {/* Conversion Rate */}
                  <div className="flex flex-col items-center gap-2 flex-1 relative group">
                    <div className="flex gap-2 items-end w-full justify-center">
                      {/* Q3 2024 Bar */}
                      <div className="relative w-16">
                        <div 
                          className="bg-amber-900 rounded-t-lg transition-all duration-300 hover:bg-amber-800 cursor-pointer"
                          style={{height: '96px'}}
                        >
                          {/* Tooltip */}
                          <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            Q3 2024: 18
                          </div>
                        </div>
                      </div>
                      {/* Q4 2024 Bar */}
                      <div className="relative w-16">
                        <div 
                          className="bg-amber-800 rounded-t-lg transition-all duration-300 hover:bg-amber-700 cursor-pointer"
                          style={{height: '160px'}}
                        >
                          {/* Tooltip */}
                          <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            Q4 2024: 30
                          </div>
                        </div>
                      </div>
                    </div>
                    <span className="text-white font-semibold text-sm mt-2">Conversion Rate</span>
                  </div>

                </div>

                {/* Legend */}
                <div className="flex justify-center gap-8 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-4 bg-amber-900 rounded"></div>
                    <span className="text-white font-semibold text-sm">Q3 2024</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-4 bg-amber-800 rounded"></div>
                    <span className="text-white font-semibold text-sm">Q4 2024</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="max-w-5xl mx-auto">
              <p className="text-white text-lg leading-relaxed">
                Comprehensive KPI tracking reveals remarkable growth across all key metrics. Skill Master's corporate client base expanded by 30% through targeted B2B campaigns. Continuous A/B testing refines messaging effectiveness and channel performance, ensuring optimal resource allocation.
              </p>
            </div>

          </div>
        </section>

        {/* The Road Ahead Section */}
        <section className="relative px-4 sm:px-8 lg:px-24 py-20">
          <div className="max-w-7xl mx-auto">
            
            {/* Heading */}
            <div className="mb-8">
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                The Road Ahead
              </h2>
              <h3 className="text-xl lg:text-2xl font-semibold text-orange-500 mb-6">
                Scaling EdTech Marketing and Beyond
              </h3>
            </div>

            {/* Description */}
            <div className="max-w-5xl mx-auto">
              <p className="text-white text-lg leading-relaxed">
                The future of EdTech marketing lies in seamlessly blending innovative technology with authentic human connections.  As we move forward, our integrated approach will continue evolving, adapting to emerging trends while maintaining the core principles that drive sustainable growth and meaningful educational impact.
              </p>
            </div>

          </div>
        </section>

        {/* Unlocking Growth Section */}
        <section className="relative px-4 sm:px-8 lg:px-24  overflow-hidden">
          {/* Background Decorative Elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-900 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            
            {/* Heading */}
            {/* Heading */}
            <div className="text-left mb-20">
              <h2 className="text-5xl font-bold text-orange-500 mb-4">
                Unlocking Growth with Integrated Strategies
              </h2>
            </div>

            {/* Modern Card Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16 max-w-6xl mx-auto">
              
              {/* Franchise Partnerships */}
              <div className="group relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center mb-6 group-hover:rotate-2 transition-transform duration-300 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white-500 mb-2">
                  Franchise Partnerships
                </h3>
                <p className="text-orange-500 text-lg">
                  Local market expansion
                </p>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-white/20 to-transparent rounded-tr-2xl"></div>
              </div>

              {/* Affiliate Networks */}
              <div className="group relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-6 group-hover:rotate-2 transition-transform duration-300 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white-500 mb-2">
                  Affiliate Networks
                </h3>
                <p className="text-orange-500 text-lg">
                  Influencer collaborations
                </p>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-white/20 to-transparent rounded-tr-2xl"></div>
              </div>

              {/* Social Engagement */}
              <div className="group relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center mb-6 group-hover:rotate-2 transition-transform duration-300 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white-500 mb-2">
                  Social Engagement
                </h3>
                <p className="text-orange-500 text-lg">
                  Community building
                </p>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-white/20 to-transparent rounded-tr-2xl"></div>
              </div>

              {/* Email Automation */}
              <div className="group relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-6 group-hover:rotate-2 transition-transform duration-300 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white-500 mb-2">
                  Email Automation
                </h3>
                <p className="text-orange-500 text-lg">
                  Personalized nurturing
                </p>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-white/20 to-transparent rounded-tr-2xl"></div>
              </div>

              {/* Direct Outreach */}
              <div className="group relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-orange-600 to-amber-700 flex items-center justify-center mb-6 group-hover:rotate-2 transition-transform duration-300 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white-500 mb-2">
                  Direct Outreach
                </h3>
                <p className="text-orange-500 text-lg">
                  Human connections
                </p>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-white/20 to-transparent rounded-tr-2xl"></div>
              </div>

              {/* Central Feature Card */}
              <div className="md:col-span-2 lg:col-span-3 bg-orange-900 backdrop-blur-md rounded-2xl p-10 border border-white/30 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                  <div className="absolute bottom-0 right-0 w-40 h-40 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
                </div>
                <div className="relative z-10 text-center">
                  <p className="text-white-900 text-xl lg:text-2xl leading-relaxed font-medium">
                    Our comprehensive marketing ecosystem combines franchise expansion, affiliate partnerships, social media engagement, email automation, and direct outreach for maximum impact. By embracing innovation while maintaining authentic connections, we're shaping the future of education.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-6 mb-12">
              <button className="group relative px-10 py-4 bg-orange-500 text-white font-bold text-lg rounded-xl hover:bg-white hover:text-orange-500 hover:scale-105 hover:shadow-2xl">
                <span className="relative z-10 transition-colors duration-300">Partner With Us</span>
              </button>
              <button className="group relative px-10 py-4 bg-transparent border-2 border-orange-500 text-white font-bold text-lg rounded-xl hover:bg-orange-500 hover:scale-105 hover:shadow-2xl">
                <span className="relative z-10 transition-colors duration-300">Explore Solutions</span>
              </button>
            </div>
          </div>
        </section>
    </div>
  );
}

export default Home;